/**
 * 图片配置 Tab（管理后台 → 图像服务）
 *
 * 平台图片运行时配置入口（超时 / worker 扫描间隔），影响所有图片生成路径：
 * 首页、API、导演模式、电商复刻、PPT、画布、照片修复。
 */

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import toast from '../Toast';
import { api, type ImageSystemConfig } from '../../services/api';

function formatMinutesPrimary(seconds: number): string {
  const minutes = seconds / 60;
  const roundedMinutes = Math.round(minutes * 10) / 10;
  return Number.isInteger(roundedMinutes) ? `${roundedMinutes} 分钟` : `${roundedMinutes.toFixed(1)} 分钟`;
}

function formatSecondsSecondary(seconds: number): string {
  return `${seconds} 秒`;
}

export default function ImageConfigTab() {
  const [config, setConfig] = useState<ImageSystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getImageSystemConfig()
      .then((res) => setConfig(res.data))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await api.updateImageSystemConfig({
        imageTaskTimeoutSeconds: config.imageTaskTimeoutSeconds,
        imageWorkerIntervalMs: config.imageWorkerIntervalMs,
      });
      setConfig(res.data);
      toast.success('已保存');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载中
      </div>
    );
  }

  const timeoutSeconds = config.imageTaskTimeoutSeconds;
  const stuckDetect = Math.round(timeoutSeconds * 1.2);
  const twoRouteBudget = Math.max(1, Math.floor(timeoutSeconds / 2));
  const threeRouteBudget = Math.max(1, Math.floor(timeoutSeconds / 3));
  const fourRouteBudget = Math.max(1, Math.floor(timeoutSeconds / 4));

  return (
    <div className="space-y-5 max-w-4xl">
      {/* 超时配置 */}
      <div className="card p-5">
        <h3 className="text-base font-semibold mb-1">图片生成超时</h3>
        <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-xs text-blue-700 leading-relaxed space-y-1">
          <p>
            <strong>此配置是全局唯一入口</strong>，影响所有图片生成路径（首页、API、导演模式、电商复刻、PPT、画布、照片修复）。
          </p>
          <p>当前规则说明：</p>
          <ul className="list-disc list-inside ml-2 space-y-0.5">
            <li>此值表示单个图片任务的总预算，按本次实际启用的路由位动态平均分配。</li>
            <li>图片路由顺序固定为：override -&gt; mode -&gt; preDefault -&gt; default。</li>
            <li>某一路由位提前失败后，后续路由位会基于剩余总预算重新平均分配。</li>
            <li>Web 卡死任务检测 = 此值 × 1.2；Worker 扫描超时 = 此值。</li>
          </ul>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              图片生成超时
              <span className="text-xs text-light-muted ml-2">（秒）</span>
            </label>
            <input
              type="number"
              min={60}
              step={60}
              value={config.imageTaskTimeoutSeconds}
              onChange={(e) =>
                setConfig((prev) =>
                  prev ? { ...prev, imageTaskTimeoutSeconds: parseInt(e.target.value, 10) || 720 } : prev
                )
              }
              className="w-full max-w-xs input text-sm sm:max-w-xs"
            />
            <p className="text-xs text-light-muted mt-1">
              默认 {formatMinutesPrimary(720)}（{formatSecondsSecondary(720)}），建议不低于 {formatMinutesPrimary(300)}（{formatSecondsSecondary(300)}）。三合一卡 img2img 模式参考图多、尺寸大，通常需要较长时间。
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Worker 扫描间隔
              <span className="text-xs text-light-muted ml-2">（毫秒）</span>
            </label>
            <input
              type="number"
              min={500}
              step={500}
              value={config.imageWorkerIntervalMs}
              onChange={(e) =>
                setConfig((prev) =>
                  prev ? { ...prev, imageWorkerIntervalMs: parseInt(e.target.value, 10) || 3000 } : prev
                )
              }
              className="w-full max-w-xs input text-sm sm:max-w-xs"
            />
            <p className="text-xs text-light-muted mt-1">
              异步图片任务 worker 的扫描周期，默认 3000 毫秒。保存后立即生效。
            </p>
          </div>

          {/* 实时推算预览 */}
          <div className="rounded-lg bg-slate-50 px-4 py-3">
            <p className="text-xs font-medium text-slate-600 mb-2">当前预算分配预览：</p>
            <div className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-4">
              <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                <div className="text-slate-500">2 段路由</div>
                <div className="text-lg font-semibold text-slate-800">{formatMinutesPrimary(twoRouteBudget)} / 段</div>
                <div className="text-slate-400">{formatSecondsSecondary(twoRouteBudget)} / 段，例如 mode -&gt; default</div>
              </div>
              <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                <div className="text-slate-500">3 段路由</div>
                <div className="text-lg font-semibold text-slate-800">{formatMinutesPrimary(threeRouteBudget)} / 段</div>
                <div className="text-slate-400">{formatSecondsSecondary(threeRouteBudget)} / 段，例如 mode -&gt; preDefault -&gt; default</div>
              </div>
              <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                <div className="text-slate-500">4 段路由</div>
                <div className="text-lg font-semibold text-slate-800">{formatMinutesPrimary(fourRouteBudget)} / 段</div>
                <div className="text-slate-400">{formatSecondsSecondary(fourRouteBudget)} / 段，例如 override -&gt; mode -&gt; preDefault -&gt; default</div>
              </div>
              <div className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200">
                <div className="text-slate-500">卡死任务检测</div>
                <div className="text-lg font-semibold text-slate-800">{formatMinutesPrimary(stuckDetect)}</div>
                <div className="text-slate-400">{formatSecondsSecondary(stuckDetect)}</div>
              </div>
            </div>
            <p className="mt-3 text-[11px] leading-5 text-slate-500">
              说明：这里展示的是“整单任务预算平均到每一路由位”的参考值。实际运行时会按当前剩余总预算和剩余路由位数动态重算，前一跳提前失败时，后一跳可获得更多剩余预算。
            </p>
          </div>
        </div>
      </div>

      {/* 保存按钮 */}
      <div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex w-full items-center justify-center gap-2 px-5 py-2 sm:w-auto"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}
